import { InputAdornment, TextField } from "@mui/material";
import type {
  FocusEventHandler,
  KeyboardEventHandler,
  ReactNode,
} from "react";
import { type FC } from "react";
import { NumericFormat } from "react-number-format";

import { inputRangeSx } from "./InputRange.styles";

// Whether a change came from the keyboard or from the value prop moving.
export type InputRangeChangeSource = "event" | "prop";

export interface IInputRangeProps {
  name?: string;
  value: number | null;
  onChange: (value: number | null, source: InputRangeChangeSource) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  error?: boolean;
  errorMessageId?: string;
  disabled?: boolean;
  inputId?: string;
  placeholder?: string;
  thousandSeparator?: boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
}

export const InputRange: FC<IInputRangeProps> = ({
  name,
  value,
  onChange,
  onBlur,
  onKeyDown,
  error,
  errorMessageId,
  disabled,
  inputId,
  placeholder = "0",
  thousandSeparator = true,
  startAdornment = <InputAdornment position="start">$</InputAdornment>,
  endAdornment,
}) => {
  return (
    <NumericFormat
      customInput={TextField}
      id={inputId}
      name={name}
      value={value ?? ""}
      variant="outlined"
      size="small"
      thousandSeparator={thousandSeparator}
      allowNegative={false}
      decimalScale={0}
      onValueChange={({ floatValue }, { source }) =>
        onChange(typeof floatValue === "number" ? floatValue : null, source)
      }
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      disabled={disabled}
      error={error}
      placeholder={placeholder}
      sx={inputRangeSx}
      slotProps={{
        input: {
          startAdornment,
          endAdornment,
        },
        htmlInput: {
          inputMode: "numeric",
          "aria-errormessage": errorMessageId,
        },
      }}
    />
  );
};
